import {
  type EcomFooterProps,
  GridItem,
  LayoutGrid,
  PdpHeader,
  ProductSelect,
  type ProductSelectProps,
} from '@jouzen/ecom-components';
import type { ReactNode, RefObject } from 'react';

import PageLayoutV3, {
  type HeaderProps,
  type PageLayoutV3Props,
} from '@/app/components/PageLayoutV3';
import type { ListProduct } from '@/queries/Products';

import type { UseRingScroller } from '../_hooks/types';
import type { Order } from '../_types/order';
import FinishSelections, {
  type FinishSelectionsProps,
} from '../FinishSelections';
import HeroCarousel, { type HeroCarouselProps } from '../HeroCarousel';
import type { OrderSummaryBaseProps } from '../OrderSummary';
import OrderSummary from '../OrderSummary';

export interface BuildHardwareOrderProps extends UseRingScroller {
  readonly carousel: HeroCarouselProps;
  readonly carouselRef: RefObject<HTMLDivElement | null>;
  readonly children?: ReactNode;
  readonly containerProps: PageLayoutV3Props['containerProps'];
  readonly finishSelectionsProps: FinishSelectionsProps;
  readonly footerProps: Partial<EcomFooterProps>;
  readonly headerProps: Partial<HeaderProps>;
  readonly monthlyMembership: ListProduct | undefined;
  readonly order: Order;
  readonly pageName: string;
  readonly productSelectData: ProductSelectProps[];
  readonly summaryProps: OrderSummaryBaseProps;
}

const BuildHardwareOrder = ({
  carousel,
  carouselRef,
  children,
  containerProps,
  finishSelectionsProps,
  focusWithinProps,
  footerProps,
  headerProps,
  monthlyMembership,
  order,
  pageName,
  productSelectData,
  sectionRefs,
  summaryProps,
}: BuildHardwareOrderProps) => {
  return (
    <PageLayoutV3
      pageName={pageName}
      containerProps={containerProps}
      footerProps={footerProps}
      headerProps={headerProps}
      Header={PdpHeader}
    >
      <LayoutGrid className="min-h-screen">
        <GridItem
          colEnd={{ sm: 'full', md: 14, lg: 16, xl: 17 }}
          colStart={{ sm: 'full' }}
        >
          <div ref={carouselRef} className="size-full">
            <div className="w-full flex-1 md:sticky md:top-24">
              <HeroCarousel {...carousel} />
            </div>
          </div>
        </GridItem>
        <GridItem
          colEnd={{ sm: 'main' }}
          colStart={{ sm: 'main', md: 15, lg: 17, xl: 18 }}
        >
          {order.ready && (
            <section className="mt-4 flex flex-col gap-y-16 md:mt-0">
              <div className="relative flex flex-col items-center md:items-stretch">
                <div
                  className="w-full max-w-sm"
                  ref={(element) => {
                    if (element) sectionRefs.current[0] = element;
                  }}
                  {...focusWithinProps}
                >
                  <FinishSelections {...finishSelectionsProps} />
                </div>
              </div>
              {productSelectData.map((data, index) => (
                <div
                  className="flex flex-col items-center md:items-stretch"
                  key={data.title}
                  ref={(element) => {
                    if (element) sectionRefs.current[index + 1] = element;
                  }}
                  {...focusWithinProps}
                >
                  <ProductSelect {...data} className="w-full" />
                </div>
              ))}
              <OrderSummary
                monthlyMembership={monthlyMembership}
                order={order}
                {...summaryProps}
              />
            </section>
          )}
        </GridItem>
      </LayoutGrid>
      {children}
    </PageLayoutV3>
  );
};

export default BuildHardwareOrder;
